/**
 * workspaceAI - thin client for the production AI endpoints used by the
 * UnifiedWorkspace. Same backend as ChatPage so billing/history/models stay
 * consistent across the app.
 *
 * Endpoints (Cloudflare Worker at api.daggpt.network):
 *   GET  /api/models                      → catalog of chat/image/video/music models
 *   POST /api/chat                        → { model, messages, userId, web_search }
 *   POST /api/generate-image              (future wiring)
 *   POST /api/generate-video              (future wiring)
 *   POST /api/generate-music              (future wiring)
 */

const API_URL = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://api.daggpt.network');

/** Fetch the full model catalog and split by category. */
export async function fetchModels() {
  const [res, defaultsRes] = await Promise.all([
    fetch(`${API_URL}/api/models?t=${Date.now()}`, { cache: 'no-cache' }),
    fetch(`${API_URL}/api/models/defaults?t=${Date.now()}`, { cache: 'no-cache' }).catch(() => null)
  ]);

  if (!res.ok) throw new Error(`models ${res.status}`);
  const data = await res.json();
  if (!data.success) throw new Error('models payload unsuccessful');

  let defaults = {};
  if (defaultsRes && defaultsRes.ok) {
    const dData = await defaultsRes.json();
    if (dData.success && dData.defaults) defaults = dData.defaults;
  }

  const mapModels = (modelsObj) => {
    return Object.values(modelsObj || {}).map(m => {
      const id = m.model_id || m.id;
      let derivedProvider = m.provider;
      if (!derivedProvider || derivedProvider.toLowerCase() === 'unknown' || derivedProvider.toLowerCase() === 'other') {
        const prefix = (id && id.includes('/')) ? id.split('/')[0] : 'Community';
        derivedProvider = prefix.charAt(0).toUpperCase() + prefix.slice(1);
      }
      
      // Sanitize backend provider strings
      if (derivedProvider && derivedProvider.toLowerCase().includes('kei.ai') || derivedProvider.toLowerCase().includes('kie.ai')) {
        derivedProvider = 'DAGGPT Network';
      }

      return {
        id,
        label:    stripProviderPrefix(m.name || id),
        provider: derivedProvider,
        badge:    pickBadge(m),
        contextWindow: m.contextWindow || m.context_window,
      };
    });
  };

  const getModels = (obj, arr, type) => {
    if (obj && Object.keys(obj).length > 0) return obj;
    return arr?.filter(m => m.category === type || (m.functions && m.functions.includes(type))) || [];
  };

  return {
    chat:  mapModels(getModels(data.chatModels, data.models, 'chat')),
    image: mapModels(getModels(data.imageModels, data.models, 'image')),
    video: mapModels(getModels(data.videoModels, data.models, 'video')),
    music: mapModels(getModels(data.musicModels, data.models, 'music')),
    defaults,
  };
}

/**
 * Send a chat request with full conversation context.
 * @param {object} p
 * @param {string} p.modelId   - resolved OpenRouter model id, e.g. "openai/gpt-4o"
 * @param {Array}  p.messages  - [{ role: 'user' | 'assistant', content: string }, …]
 * @param {string} [p.userId]  - optional authenticated user id
 * @param {boolean}[p.webSearch]
 * @returns {Promise<{ text: string, modelUsed: string, webSearchUsed?: boolean }>}
 */
export async function sendChat({ modelId, messages, userId = '', webSearch = false }) {
  const res = await fetch(`${API_URL}/api/unified/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-user-id': userId || '',
    },
    body: JSON.stringify({
      model: modelId,
      messages,
      userId,
      web_search: webSearch,
    }),
  });

  if (!res.ok) {
    let err = `chat ${res.status}`;
    try { const b = await res.json(); err = b.error || err; } catch {}
    throw new Error(err);
  }

  const data = await res.json();
  return {
    text: data.response || '',
    modelUsed: data.modelUsed || modelId,
    webSearchUsed: !!data.webSearchUsed,
  };
}

/**
 * Stream a chat request with full conversation context.
 * Provides real-time updates for content and status events.
 */
export async function sendChatStream({ modelId, messages, userId = '', webSearch = false, localTime, onUpdate, signal }) {
  console.log('[WorkspaceAI] sendChatStream Request:', { modelId, userId, webSearch, messageCount: messages.length, url: `${API_URL}/api/unified/chat/stream` });
  const res = await fetch(`${API_URL}/api/unified/chat/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-user-id': userId || '',
    },
    body: JSON.stringify({
      model: modelId,
      messages,
      userId,
      web_search: webSearch,
      local_time: localTime,
    }),
    signal,
  });

  console.log('[WorkspaceAI] sendChatStream Response Status:', res.status);

  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.error || `Stream failed: ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let hasTriggeredDone = false;
  let idleTimeoutId = null;

  const triggerDone = () => {
    if (idleTimeoutId) clearTimeout(idleTimeoutId);
    if (hasTriggeredDone) return;
    hasTriggeredDone = true;
    onUpdate({ type: 'done' });
  };

  const resetIdleTimeout = () => {
    if (idleTimeoutId) clearTimeout(idleTimeoutId);
    idleTimeoutId = setTimeout(() => {
      console.warn('[sendChatStream] Stream idle timeout (10s) triggered. Forcing stream completion.');
      triggerDone();
      try { reader.cancel(); } catch (e) {}
    }, 10000); // 10 seconds of silence from the stream forces done
  };

  try {
    resetIdleTimeout(); // Start initial timeout
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      resetIdleTimeout(); // Reset timeout on successful chunk read

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop();

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const raw = line.slice(6).trim();
        if (raw === '[DONE]') {
          triggerDone();
          return;
        }

        try {
          const ev = JSON.parse(raw);
          onUpdate(ev);
        } catch (e) {
          console.warn('[stream] parse error', e, raw);
        }
      }
    }
  } finally {
    if (idleTimeoutId) clearTimeout(idleTimeoutId);
    triggerDone();
  }
}

/**
 * Send an image, video, or music generation request with cross-modal memory.
 * Uses the backend Memory Synthesizer to distill context into a single prompt.
 */
export async function generateUnified({ mode, modelId, messages, options = {}, userId = '' }) {
  const res = await fetch(`${API_URL}/api/unified/generate`, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      'x-user-id': userId || '',
    },
    body: JSON.stringify({ mode, modelId, messages, options, userId }),
  });

  if (!res.ok) {
    let err = `unified ${res.status}`;
    try { const b = await res.json(); err = b.error || err; } catch {}
    throw new Error(err);
  }

  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Unified generation failed');
  return { taskId: data.taskId, synthesizedPrompt: data.synthesizedPrompt };
}

/**
 * Build the `messages` array the chat endpoint expects from a list of Turns.
 * Cross-modality memory is preserved:
 *   • user turns of any type → 'user' message with their prompt (+ modality tag)
 *   • assistant chat turns   → 'assistant' message with their text
 *   • assistant image/video/music turns → synthesized 'assistant' note that
 *     RECITES the user prompt that produced them, so when the next user says
 *     "make a video of it" the chat model can resolve "it".
 */
/**
 * Build the `messages` array the chat endpoint expects from a list of Turns.
 * Cross-modality memory is preserved:
 *   • user turns of any type → 'user' message with their prompt (+ modality tag)
 *   • assistant chat turns   → 'assistant' message with their text
 *   • assistant image/video/music turns → synthesized 'assistant' note that
 *     RECITES the user prompt that produced them, so when the next user says
 *     "make a video of it" the chat model can resolve "it".
 */
export function buildMessagesFromTurns(turns, newUserPrompt, currentAttachments = []) {
  // Clear context history prior to any "new thread" user messages
  let relevantTurns = turns;
  const newThreadIdx = [...turns].reverse().findIndex(t => 
    t.role === 'user' && t.text && [
      'new thread',
      'new conversation',
      'start a new thread',
      'start new thread',
      'start a new conversation',
      'start new conversation',
      'clear thread',
      'clear chat',
      'clear conversation',
      '/new',
      '/newthread',
      '/clear'
    ].includes(t.text.trim().toLowerCase())
  );
  if (newThreadIdx !== -1) {
    const originalIdx = turns.length - 1 - newThreadIdx;
    relevantTurns = turns.slice(originalIdx);
  }

  const messages = [];
  let lastUserPrompt = '';

  for (const t of relevantTurns) {
    if (t.role === 'user') {
      const prefix = t.type === 'chat' ? '' : `(${t.type} request) `;
      lastUserPrompt = t.text || '';
      
      const content = [{ type: 'text', text: `${prefix}${lastUserPrompt}` }];
      
      // Include any attachments or images from this turn
      if (t.meta?.attachments && Array.isArray(t.meta.attachments)) {
        t.meta.attachments.forEach(a => {
          if (a.type?.startsWith('image/') || a.url?.match(/\.(jpg|jpeg|png|webp|gif)$/i)) {
            content.push({ type: 'image_url', image_url: { url: a.url } });
          } else {
            // For other files, add a text reference so the AI knows it's there
            let fileNote = `\n[Attached File: ${a.name || 'Untitled'}]`;
            if (a.extractedText) {
              fileNote += `\n[Extracted Content]:\n${a.extractedText}`;
            }
            content[0].text += fileNote;
          }
        });
      }

      messages.push({ role: 'user', content });
      continue;
    }
    // assistant
    if (t.status !== 'done') continue;
    if (t.type === 'chat' && t.text) {
      messages.push({ role: 'assistant', content: t.text });
    } else if (t.type === 'image' && t.imageUrl) {
      messages.push({ role: 'assistant', content:
        `[Context: I previously generated an image for the prompt: "${lastUserPrompt}". This is for your reference only.]` });
    } else if (t.type === 'video' && t.videoUrl) {
      messages.push({ role: 'assistant', content:
        `[Context: I previously generated a video for the prompt: "${lastUserPrompt}". This is for your reference only.]` });
    } else if (t.type === 'music' && t.audioUrl) {
      messages.push({ role: 'assistant', content:
        `[Context: I previously generated a music track for the prompt: "${lastUserPrompt}". This is for your reference only.]` });
    }
  }

  // Add the final user prompt with current attachments
  const finalContent = [{ type: 'text', text: newUserPrompt }];
  if (currentAttachments && currentAttachments.length > 0) {
    currentAttachments.forEach(a => {
      if (a.type?.startsWith('image/') || a.url?.match(/\.(jpg|jpeg|png|webp|gif)$/i)) {
        finalContent.push({ type: 'image_url', image_url: { url: a.url } });
      } else {
        // For other files, add a text reference so the AI knows it's there
        let fileNote = `\n[System Note: User has attached a document file named "${a.name || 'Untitled'}".]`;
        if (a.extractedText) {
          fileNote += `\n[Extracted Content of ${a.name}]:\n${a.extractedText}\n[End of ${a.name} content]`;
        } else {
          fileNote += `\n[Note: This file type (e.g. PDF) cannot be read directly by you yet. If the user asks for a summary, acknowledge the file name but explain you need them to copy-paste the text or use a text/csv/md file instead.]`;
        }
        finalContent[0].text += fileNote;
      }
    });
  }
  messages.push({ role: 'user', content: finalContent });

  return messages;
}

/**
 * detectIntent - classify a draft prompt's intended modality.
 *
 * Returns: { mode: 'chat'|'image'|'video'|'music', confidence: 0..1, source: 'slash'|'keyword'|'pronoun'|'default', referenceTurnId?: string }
 *
 * Strategy (cheap, no LLM call):
 *   1. Slash command (/image /video /music /chat) → confidence 1.0
 *   2. Strong keyword match → confidence 0.85
 *   3. Pronoun referral ("it"/"that"/"this") + mode keyword → use prior turn type
 *   4. Pronoun referral alone → match the most recent assistant turn's type
 *   5. Default → 'chat'
 *
 * `recentTurns` is the conversation's turns array; used for pronoun resolution.
 */
export function detectIntent(prompt, recentTurns = []) {
  const txt = (prompt || '').trim();
  if (!txt) return { mode: 'chat', confidence: 1, source: 'default' };

  // 1) Slash command (highest confidence, explicit user intent)
  const slash = txt.match(/^\s*\/(chat|image|video|music|slides)\b/i);
  if (slash) {
    return { mode: slash[1].toLowerCase(), confidence: 1, source: 'slash' };
  }

  const lc = txt.toLowerCase();

  // 2) Strong keyword match
  const TEXT_KW = /\b(script|story|article|post|email|content|text|code|formula|strategy|plan|list|menu|report|summary|essay|poem|hook|copy|dialogue|draft)\b/;
  const KW = {
    video: /\b(video|clip|animation|animate|movie|footage|short film|reel|gif)\b/,
    music: /\b(music|song|track|beat|tune|melody|soundtrack|jingle|composition|instrumental|lo[\s-]?fi)\b/,
    image: /\b(image|picture|illustration|render|painting|photo(graph)?|portrait|poster|drawing|sketch|artwork|design|mockup|wallpaper|logo)\b/,
    slides: /\b(slides|presentation|powerpoint|keynote|deck|ppt|pptx)\b/,
  };
  // Action verbs that strongly suggest generation
  const ACTION = /\b(create|generate|make|design|render|draw|paint|illustrate|compose|produce|visualize|imagine|show me|give me)\b/;
  
  // Action verbs that suggest chat/conversation
  const CHAT_ACTION = /\b(who|what|where|when|why|how|tell|explain|write|summarize|analyze|help|search|find|translate|calculate|hi|hello|hey)\b/;
  
  const hasTextKW = TEXT_KW.test(lc);
  const hasAction = ACTION.test(lc);
  const hasChatAction = CHAT_ACTION.test(lc);

  // 2.1) Explicit Chat check - if it looks like a text-heavy request, favor chat
  if (hasChatAction || hasTextKW) {
    // If they explicitly asked for a script/text, we default to chat 
    // unless they also explicitly used a generation verb for a visual asset.
    const genVisual = /\b(create|generate|make|render|draw|paint|illustrate|compose|produce)\b\s+(a\s+|an\s+|the\s+)?(image|video|picture|photo|illustration|logo|song|track|beat|clip|movie)\b/.test(lc);
    
    if (!genVisual) {
      // Check if they explicitly asked for slides/presentations
      if (KW.slides.test(lc)) {
        return { mode: 'slides', confidence: 0.90, source: 'keyword' };
      }
      return { mode: 'chat', confidence: 0.88, source: 'keyword' };
    }
  }

  for (const mode of ['video', 'music', 'image', 'slides']) {
    if (KW[mode].test(lc)) {
      // Higher confidence if also paired with a generation action verb.
      return {
        mode,
        confidence: hasAction ? 0.92 : 0.78,
        source: 'keyword',
      };
    }
  }

  // 3) Pronoun referral - "make one of it", "video of that", "draw this"
  const PRONOUN = /\b(it|that|this|them|those|the (image|video|song|music|track|picture|photo))\b/;
  const hasPronoun = PRONOUN.test(lc);
  if (hasPronoun && (hasAction || hasChatAction)) {
    // Find the most recent assistant turn (any modality) to reference.
    const lastAssistant = [...recentTurns].reverse().find(
      t => t.role === 'assistant' && t.status === 'done'
    );
    if (lastAssistant) {
      // Check if user is asking for a DIFFERENT modality than the last asset
      // (e.g. last was image, user says "now a video of it")
      const askedMode = (() => {
        if (/\b(video|clip|animation)\b/.test(lc)) return 'video';
        if (/\b(music|song|track|beat)\b/.test(lc)) return 'music';
        if (/\b(image|picture|photo|render|illustration)\b/.test(lc)) return 'image';
        return null;
      })();
      
      // If they ask "what is it?" (chat action + pronoun), we should go to chat
      if (hasChatAction && !askedMode) {
        return { mode: 'chat', confidence: 0.8, source: 'pronoun', referenceTurnId: lastAssistant.id };
      }

      return {
        mode: askedMode || (lastAssistant.type === 'chat' ? 'image' : lastAssistant.type),
        confidence: 0.70,
        source: 'pronoun',
        referenceTurnId: lastAssistant.id,
      };
    }
  }

  // 4) Default → chat (increased confidence slightly to 0.7)
  return { mode: 'chat', confidence: 0.7, source: 'default' };
}

// ─── utils ─────────────────────────────────────────────────────
function stripProviderPrefix(name) {
  // e.g. "OpenAI: GPT-4o" → "GPT-4o"
  const idx = name.indexOf(':');
  return idx > 0 ? name.slice(idx + 1).trim() : name;
}

function pickBadge(m) {
  // 1. Check if model has an explicit tier from database that acts as a badge
  const dbTier = (m.tier || '').toUpperCase();
  if (dbTier && ['FLAGSHIP', 'REASONING', 'FAST', 'OPEN', 'LONG CTX', 'PRO', 'PREMIUM'].includes(dbTier)) {
    return dbTier;
  }

  // 2. Fallback heuristic flags for the model picker
  const id = (m.model_id || m.id || '').toLowerCase();
  const name = (m.name || m.display_name || '').toLowerCase();
  if (id.includes('gpt-4o') || id.includes('gpt-5') || name.includes('flagship')) return 'FLAGSHIP';
  if (id.includes('claude') && id.includes('sonnet')) return 'REASONING';
  if (id.includes('gemini') && id.includes('pro')) return 'LONG CTX';
  if (id.includes('flash') || id.includes('haiku') || id.includes('mini')) return 'FAST';
  if (id.includes('llama') || id.includes('mistral') || id.includes('qwen') || id.includes('deepseek')) return 'OPEN';
  return '';
}
